/* ============================================================
   EXON ANCHOR CASE — the lit display case, and the write behind the pick.

   The three-bay vitrine the owner approved (master section 4), driven onto
   the shell in snippets/drops-anchor-case.liquid at every mount that renders
   it (popup, drawer, cart page). One island for all of them, because they
   answer the same question from the same fact and a second reader is a
   second answer — the same rule drops-ticket.js runs on.

   THIS FILE FETCHES NOTHING. drops.js is and stays the sole caller of the
   drops API; exon-store.js is and stays the cart network owner. The read
   arrives through window.EXON.drops.getChoice(); the WRITE goes back out
   through window.EXON.drops.requestAnchorOffer / pickAnchorOption /
   attachChoiceGrant. This island holds no endpoint, no session ref and no
   fetch, which is why every action below is a promise handed to somebody
   else.

   -- LOAD ORDER ----------------------------------------------------
   This file reads state drops.js publishes and formats time with the bar
   island's countdown, so it MUST execute after both. Its tag in
   layout/theme.liquid is `defer` and sits after them: defer preserves
   DOCUMENT ORDER at execution, so position in the file is the guarantee.
   `async` would break this SILENTLY — it executes on arrival, so it would
   beat drops-bar.js on a fast connection and lose on a slow one.

   -- EXACTLY 3, 2 OR 1 ---------------------------------------------
   The accepted behaviour is three REAL server choices, or two, or one when
   fewer are available. So the bay count is the offer's `option_count`, the
   copy switches on that same number, and an offer whose count and option
   list disagree is refused whole by drops.js before it ever arrives here.
   There is no sample product, no padding bay and no third promise over two
   real options.

   -- WHAT A PICK SUBMITS -------------------------------------------
   `option_id`, which is the offer's own identity — never a variant_gid
   substituted for it. A stale or refused pick reconciles /state and says
   so; it never replaces the customer's selection locally and never mutates
   an already committed grant. Requesting a fresh offer after a stale one is
   the customer's move through the real caller, not an automatic retry.

   -- THE STATES, enumerated from what can actually be reached ------
     no anchor state / not entitled     NOTHING AT ALL
     entitled, no offer yet             the folded line: promise + clock
     an open offer                      the case, pickable
     an open offer under a condition
       whose eligible subtotal is known
       and below it                     the case, LOCKED
     a write in flight                  committing
     a recorded grant, code bound       the 66px receipt, attachable
     a recorded grant, issuance pending PENDING — never a code, never a claim
     a stale / refused write            the case, with the truth on it

   -- THE FOLDED LINE IS THE SHARED ROW (EXO-4824) -------------------
   Every mount sits inside a CONTROLLED fold row (snippets/fold-row.liquid,
   key 'anchor'; window.ExonFold, assets/fold.js). The row is the one
   control. This island owns its state and its side effects, so the row's
   tap is a REQUEST (`xf:toggle`) the island answers by COMMITTING
   (ExonFold.open / close); the row's disclosure is read back from the row
   on every paint, never kept in a flag of our own. Each paint also writes
   the folded line itself through ExonFold.set: whether it shows at all,
   its title, its clock or state word, its status and its icon. Contract:
   docs/exo-4667/DROP-ADOPTION.md; rulings R4–R9, R11 in
   docs/exo-4824/BUILD-PLAN.md.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropAnchorInit) return;
  window.__exonDropAnchorInit = true;
  window.EXON = window.EXON || {};

  /* ---------------- faults ----------------
     Same attribute and same console channel as drops.js, drops-bar.js and
     drops-ticket.js, so an operator reads one signal whichever part of the
     stack broke. The customer surface still degrades to nothing; what must
     never happen is "broken" and "this customer has no drop" looking
     identical from outside. */
  var said = {};
  var REPEATS = {
    anchor_not_renderable: true,
    anchor_writer_unavailable: true,
    anchor_bar_unavailable: true
  };
  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    if (REPEATS[name]) {
      if (said[name]) return;
      said[name] = true;
    }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }

  /* ---------------- tiny DOM helpers ---------------- */
  function all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function setText(node, value) {
    if (!node) return;
    if (node.textContent !== value) node.textContent = value;
  }
  function show(node, on) { if (node) node.hidden = !on; }
  /* text that is about to become markup: the row title and the veil carry
     an amount in its own bidi-isolated run, so they are written as HTML,
     and every word around that run is escaped first */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* a copy template from the shell with its one {slot} filled by trusted
     markup. The template is escaped as text; only the slot is markup. */
  function fillCopy(tpl, slot, html) {
    return esc(tpl).split(slot).join(html);
  }

  /* A server-supplied string is about to become part of a CSS url(). Anything
     that could close the url() or the declaration is refused outright rather
     than escaped: art is decoration, so dropping a suspicious one costs a lit
     but empty plinth, and there is no value in being clever here. */
  function coverValue(url) {
    if (typeof url !== 'string' || !url) return '';
    if (!/^(https?:\/\/|\/)[^\s"')\\]+$/.test(url)) return '';
    return 'url("' + url + '")';
  }
  /* the accent rides straight into rgba(), so it is three numbers or nothing */
  function accentValue(a) {
    if (typeof a !== 'string') return '';
    return /^\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*$/.test(a) ? a : '';
  }

  /* the grant names itself through the option that produced it when the
     server projected one; otherwise it names nothing. An invented product
     name on a receipt is a promise about money. */
  function optionForVariant(choice, variantGid) {
    var offer = choice && choice.offer;
    if (!offer || !variantGid) return null;
    for (var i = 0; i < offer.options.length; i++) {
      if (offer.options[i].variant_gid === variantGid) return offer.options[i];
    }
    return null;
  }

  /* ============================================================
     derive — PURE. No DOM, no globals, no clock of its own: the normalised
     choice block in, what the surface should show out, or the REASON there
     is nothing to show.

     Split out for the same reason the bar splits validate and compose, and
     drops-ticket.js splits this: the decisions worth testing are these, and
     they must be reachable without a DOM.

     ctx = {
       mechanic, windowEndAt,          the campaign's own facts
       action,                         the choice action drops.js published
       conditionMinAgorot,             server condition block, or null
       eligibleSubtotalAgorot          the ACTUAL eligible subtotal, or null
       minimumQuantity                 authored buy quantity, or null
       eligibleQuantity                units on the eligible lines, or null
     }
     ============================================================ */
  function derive(choice, nowMs, countdown, ctx) {
    ctx = ctx || {};
    if (ctx.mechanic !== 'anchor_reward') return { view: null, reason: 'not_anchor' };
    if (!choice || typeof choice !== 'object') return { view: null, reason: 'no_choice' };

    var action = (ctx.action && typeof ctx.action === 'object') ? ctx.action : { state: 'idle' };

    /* THE RECORDED GRANT WINS OVER EVERYTHING. A committed choice is not
       re-offered, re-picked or replaced: `already_recorded` is the server's
       own answer to a second ask, and this surface must not present a case
       that cannot be used. */
    var grant = null;
    var grants = Array.isArray(choice.grants) ? choice.grants : [];
    for (var g = 0; g < grants.length; g++) {
      if (grants[g].mechanic !== 'anchor_reward') continue;
      if (grants[g].state === 'failed' || grants[g].state === 'void') continue;
      grant = grants[g];
    }

    var left = countdown ? (countdown(ctx.windowEndAt || null, nowMs) || '') : '';

    /* THE CONDITION. `conditionMinAgorot` is the server's number and the only
       one that may be shown. Whether it is MET is a separate fact: a cart
       total is not evidence that a collection-scoped condition is satisfied,
       so "reached" is claimed only from an actual eligible subtotal. When
       that is unavailable the condition is DESCRIBED and nothing is claimed —
       a withheld benefit is worse than an unlocked one, and a false reached
       state is worse than both. */
    var min = (typeof ctx.conditionMinAgorot === 'number' && isFinite(ctx.conditionMinAgorot) &&
      ctx.conditionMinAgorot >= 0 && Math.floor(ctx.conditionMinAgorot) === ctx.conditionMinAgorot)
      ? ctx.conditionMinAgorot : null;
    var eligible = (typeof ctx.eligibleSubtotalAgorot === 'number' &&
      isFinite(ctx.eligibleSubtotalAgorot) && ctx.eligibleSubtotalAgorot >= 0)
      ? ctx.eligibleSubtotalAgorot : null;
    /* THE AUTHORED UNIT MINIMUM, the same conjunction as the goal rail: a
       known integer minimum and a measured eligible quantity below it lock
       the case even when the amount is met. No minimum (an older server) is
       today's amount-only rule exactly; an unknown quantity never locks. */
    var minQty = (typeof ctx.minimumQuantity === 'number' && isFinite(ctx.minimumQuantity) &&
      ctx.minimumQuantity >= 1 && Math.floor(ctx.minimumQuantity) === ctx.minimumQuantity)
      ? ctx.minimumQuantity : null;
    var qty = (typeof ctx.eligibleQuantity === 'number' && isFinite(ctx.eligibleQuantity) &&
      ctx.eligibleQuantity >= 0 && Math.floor(ctx.eligibleQuantity) === ctx.eligibleQuantity)
      ? ctx.eligibleQuantity : null;
    var amountShort = min !== null && eligible !== null && eligible < min;
    var itemsShort = min !== null && eligible !== null && minQty !== null && qty !== null && qty < minQty;
    var unmet = amountShort || itemsShort;
    var condition = min === null ? null : {
      minimum_subtotal_agorot: min,
      /* 'under' only when we can actually see the shortfall; otherwise the
         plain description, which claims nothing either way. The LOCKED case
         narrows 'under' to 'left' below. */
      wording: unmet ? 'under' : 'plain',
      /* amount-only: 0 when only the items are short, never a borrowed gap */
      remaining_agorot: unmet ? Math.max(0, min - eligible) : null,
      remaining_items: itemsShort ? (minQty - qty) : null
    };

    if (grant) {
      var made = grant.display && grant.display.variant_gid === grant.variant_gid ?
        grant.display : optionForVariant(choice, grant.variant_gid);
      return {
        reason: '',
        view: {
          /* READINESS AND DISCLOSURE ARE TWO FACTS. Pending is what a bound
             benefit whose provider issuance has not landed looks like, and it
             names no code because there is none to name. */
          state: grant.code_available ? 'chosen' : 'pending',
          count: 0,
          bays: [],
          condition: condition,
          left: left,
          open: true,
          tease: false,
          receipt: {
            choice_grant_id: grant.choice_grant_id,
            name: made ? (made.title || made.name || '') : '',
            cover: coverValue(made ? made.art_url : ''),
            code_available: grant.code_available === true,
            /* the ONLY thing that may be applied is a code the server
               actually disclosed; `attachable` says exactly that and nothing
               about whether Shopify will accept it. */
            attachable: grant.code_available === true &&
              typeof grant.code === 'string' && grant.code !== '',
            no_expiry: grant.no_expiry === true,
            ends_at: grant.ends_at || null
          }
        }
      };
    }

    var offer = choice.offer;
    var busy = action.pending === true;
    var stale = !busy && (action.state === 'stale' ||
      (action.state === 'refused' && action.code !== 'choice_unavailable'));

    if (!offer) {
      /* No offer yet is NOT an incident: an Anchor campaign the member is
         admitted to opens its offer when the customer opens the case, and a
         campaign with nothing to advertise right now answers `no_offer`. The
         folded line still carries the promise and the clock. */
      return {
        reason: '',
        view: {
          state: busy ? 'committing' : (stale ? 'stale' : 'open'),
          count: 0,
          bays: [],
          condition: condition,
          left: left,
          open: false,
          tease: false,
          receipt: null
        }
      };
    }

    /* the offer's own deadline, independent of the campaign window */
    var offerEnd = Date.parse(offer.expires_at);
    if (isFinite(offerEnd) && offerEnd <= nowMs) {
      return {
        reason: '',
        view: {
          state: 'stale', count: 0, bays: [], condition: condition,
          left: left, open: false, tease: false, receipt: null
        }
      };
    }

    var bays = [];
    for (var i = 0; i < offer.options.length; i++) {
      var o = offer.options[i];
      bays.push({
        option_id: o.option_id,
        /* the server's own words, never a fabricated product. An option with
           no title keeps a lit plinth and an empty plate rather than
           borrowing another product's name. */
        name: o.title || o.name || '',
        sub: o.sub || o.subtitle || '',
        cover: coverValue(o.art_url),
        accent: accentValue(o.accent)
      });
    }

    var state = busy ? 'committing' : (unmet ? 'locked' : (stale ? 'stale' : 'open'));
    /* THE LOCKED HEADER NAMES THE MONEY STILL MISSING ('left'), and only
       while some is: items-only short keeps the threshold ('under'), so the
       line never reads "₪0 more". Every other state — a recorded gift
       included — keeps the description, never an instruction to spend. */
    if (state === 'locked' && amountShort) {
      condition = {
        minimum_subtotal_agorot: condition.minimum_subtotal_agorot,
        wording: 'left',
        remaining_agorot: condition.remaining_agorot,
        remaining_items: condition.remaining_items
      };
    }

    return {
      reason: '',
      view: {
        state: state,
        count: bays.length,
        bays: bays,
        condition: condition,
        left: left,
        open: true,
        tease: false,
        receipt: null
      }
    };
  }

  /* ------------------------------------------------------------
     THE ONE READ and THE ONE WRITE PATH.
     ------------------------------------------------------------ */
  function bar() {
    var b = window.EXON.bar;
    return (b && typeof b.countdown === 'function') ? b : null;
  }
  function drops() {
    var d = window.EXON.drops;
    return (d && typeof d.getChoice === 'function') ? d : null;
  }
  /* A case a customer can click that cannot record the click is worse than no
     case: it takes a choice it cannot keep. Without the writer there is no
     case — the same fail-closed rule the mystery hand runs on. */
  function writer() {
    var d = window.EXON.drops;
    return (d && typeof d.pickAnchorOption === 'function' &&
      typeof d.requestAnchorOffer === 'function') ? d : null;
  }
  function state() {
    var d = window.EXON.drops;
    return (d && d.state) || null;
  }
  function entitled() {
    var s = state();
    return !!(s && s.entitled);
  }

  function current(nowMs) {
    var d = drops();
    if (!d) return { view: null, reason: 'no_client' };
    var b = bar();
    if (!b) return { view: null, reason: 'no_registry' };
    if (!writer()) return { view: null, reason: 'no_writer' };
    var s = state();
    var progress = typeof d.getChoiceProgress === 'function' ? d.getChoiceProgress() : null;
    try {
      return derive(d.getChoice(), nowMs, b.countdown, {
        mechanic: s ? s.mechanic : null,
        windowEndAt: s ? s.window_end_at : null,
        action: typeof d.getChoiceAction === 'function' ? d.getChoiceAction() : null,
        conditionMinAgorot: progress ? progress.minimum_subtotal_agorot :
          ((s && s.condition) ? s.condition.minimum_subtotal_agorot : null),
        eligibleSubtotalAgorot: progress ? progress.eligible_subtotal_agorot : null,
        minimumQuantity: progress ? progress.minimum_quantity : null,
        eligibleQuantity: progress ? progress.eligible_quantity : null
      });
    } catch (e) {
      fault('anchor_read_failed', e);
      return { view: null, reason: 'read_failed' };
    }
  }

  function report(reason) {
    if (!reason) return;
    if (reason === 'not_anchor') return;        /* a different campaign type */
    if (reason === 'no_choice') return;         /* no choice session at all */
    if (reason === 'no_client') return;
    if (!entitled()) return;                    /* no drop is not an incident */
    if (reason === 'read_failed') return;       /* already emitted, with the throw */
    if (reason === 'no_registry') { fault('anchor_bar_unavailable', ''); return; }
    if (reason === 'no_writer') { fault('anchor_writer_unavailable', ''); return; }
    var s = state();
    fault('anchor_not_renderable', (s && s.campaign_id) || '');
  }

  /* agorot to a plain shekel figure. The theme's money filter is Liquid's and
     this runs after render, so the figure is composed here — bidi-isolated by
     the shell's own .ltr run, never by punctuation guessing. */
  function money(agorot) {
    if (typeof agorot !== 'number' || !isFinite(agorot)) return '';
    var whole = Math.round(agorot) / 100;
    var text = (Math.round(whole * 100) / 100).toString();
    return '₪' + text;
  }

  /* ============================================================
     THE PANEL — one mount's worth of surface.
     ============================================================ */
  /* the row's lock icon, as fold.js keeps its own icons: markup, not copy
     (the same glyph as snippets/icon-upsell.liquid 'lock') */
  var LOCK_ICON = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';

  /* THE ROW'S STATUS PER STATE (BUILD-PLAN.md R8). 'action' is a case still
     waiting for the customer; a pending grant is NOT 'done', because a
     benefit whose issuance has not landed is not a disclosed reward. */
  var ROW_STATUS = {
    open: 'action', committing: 'action', stale: 'action',
    locked: 'locked', chosen: 'done', pending: 'idle'
  };

  function fold() {
    var f = window.ExonFold;
    return (f && typeof f.set === 'function') ? f : null;
  }

  function Panel(mount) {
    var panel = mount.querySelector('[data-gvc-panel]');
    var tpl = mount.querySelector('[data-gvc-bay-tpl]');
    var caseEl = mount.querySelector('[data-gvc-case]');
    var live = mount.querySelector('[data-gvc-live]');
    var mini = mount.querySelector('[data-gvc-mini]');
    var attach = mount.querySelector('[data-gvc-attach]');
    var kept = mount.querySelector('[data-gvc-kept]');
    var teaseNode = mount.querySelector('[data-gvc-tease]');
    var titles = mount.querySelector('[data-gvc-titles]');
    var words = mount.querySelector('[data-gvc-words]');
    var surface = mount.getAttribute('data-gvc-surface') || 'popup';

    if (!panel || !tpl || !caseEl) return null;

    /* copied in drops-mystery.js Panel() and drops-ticket.js rowOf(): keep in step (EXO-4824 D-1) */
    /* THE ROW this mount sits in, found once: the mount is never moved out
       of it, and a mount rendered without one (no fold row on the page)
       simply has no folded line to write and no control to answer. */
    var row = mount.closest ? mount.closest('[data-xf]') : null;
    var rowBtn = null;
    if (row) {
      for (var c = row.firstElementChild; c; c = c.nextElementSibling) {
        if (c.classList && c.classList.contains('xf-row')) { rowBtn = c; break; }
      }
    }
    var rowState = rowBtn ? rowBtn.querySelector('[data-xf-state]') : null;

    var bays = [];
    var drawn = '';
    var veilDrawn = null;

    /* OPEN IS THE CUSTOMER'S MOVE, and it is the ROW's state, per mount: the
       popup teases and the cart chooses, so each surface's row answers for
       itself. Read back from the row on every paint, never a flag of ours,
       so the controller's own silent fold at boot (fold.js ready()) and a
       group close by another row are both simply true here. Without the
       controller the row is server-rendered open (the no-JS floor), and that
       is what this reads. */
    function rowOpen() {
      if (!row) return false;
      var f = fold();
      return f ? f.isOpen(row) === true : row.getAttribute('data-xf-open') === 'true';
    }

    /* THE POPUP TEASES. Opening the case there drops the visible cross-sell
       offers from 2.0 to 0.7, so the control points at the cart instead —
       EXCEPT in the popup's own [data-no-offers] mode, where there is no
       cross-sell to protect and the case may own the popup. */
    function teasing() {
      if (surface !== 'popup') return false;
      return !(mount.closest && mount.closest('[data-no-offers]'));
    }

    function openCart() {
      try {
        document.dispatchEvent(new CustomEvent('exon:drawer:open'));
      } catch (e) { /* the drawer is optional */ }
    }

    function announce(text) {
      if (!live || !text) return;
      if (live.textContent !== text) live.textContent = text;
    }

    function bayFor(index) {
      while (bays.length <= index) {
        var frag = tpl.content ? tpl.content.cloneNode(true) : null;
        var node = frag ? frag.querySelector('.gvc-bay') : null;
        if (!node) return null;
        node.addEventListener('click', onPick);
        caseEl.appendChild(node);
        bays.push(node);
      }
      return bays[index];
    }

    function onPick(event) {
      var node = event.currentTarget;
      var id = node.getAttribute('data-gvc-option');
      var w = writer();
      if (!id || !w) return;
      /* ONE IN-FLIGHT OPERATION. drops.js holds the real lock; this only
         stops the same panel queueing a second click behind it. */
      if (node.getAttribute('aria-disabled') === 'true') return;
      announce(node.getAttribute('data-gvc-announce') || '');
      w.pickAnchorOption(id);
    }

    /* THE CUSTOMER OPENS THE CASE: commit first, and only an open that took
       runs the old side effect. A refused open (another row of the surface
       would not close) changed nothing, so it must not spend a request. */
    function openRequest() {
      /* the tease is decided at runtime ([data-no-offers]); a tap that got
         here while the row's go mark was a paint behind still leads to the
         cart, never into a case the popup must not open */
      if (teasing()) { openCart(); return; }
      var f = fold();
      if (!f || !row || f.open(row) !== true) { repaintOne(Date.now()); return; }
      var w = writer();
      var d = drops();
      var choice = d ? d.getChoice() : null;
      /* THE OFFER IS REQUESTED THROUGH THE REAL CALLER, on the customer's
         own action, and only when there is not already one open. */
      if (w && choice && !choice.offer) w.requestAnchorOffer();
      repaintOne(Date.now());
    }

    /* THE ROW ASKS (`xf:toggle`, fold.js): a customer tap either way, or the
       surface's group closing this row because another one is opening. The
       group close is SYNCHRONOUS — the controller reads this row's state the
       moment dispatchEvent returns — so the close path awaits nothing,
       requests nothing and deals nothing. Its ONE refusal is a pick being
       saved: folding then would hide the customer's own choice mid-write.
       A refused tap says so in words; a refused group close leaves the
       other row closed, which is the controller's answer, not ours. */
    function onRowToggle(event) {
      if (event.target !== row) return;
      var want = event.detail ? event.detail.want : undefined;
      var reason = event.detail ? event.detail.reason : '';
      if (want === true) { openRequest(); return; }
      if (want !== false) return;
      var r = current(Date.now());
      if (r.view && r.view.state === 'committing') {
        if (reason === 'tap') announce(wordFor('committing'));
        return;
      }
      var f = fold();
      if (f) f.close(row);
      repaintOne(Date.now());
    }

    /* THE ROW LEADS ELSEWHERE (`xf:go`): the popup's tease. Its tap opens the
       cart and never the case. A go mark one paint stale (the popup just
       entered its own [data-no-offers] mode) is an ordinary open request. */
    function onRowGo(event) {
      if (event.target !== row) return;
      if (teasing()) { openCart(); return; }
      openRequest();
    }

    /* every commit on the row, ours or anyone's, repaints the inner case so
       the two disclosures can never disagree */
    function onRowCommit(event) {
      if (event.target !== row) return;
      repaintOne(Date.now());
    }

    function onAttach() {
      var d = window.EXON.drops;
      var id = mini ? mini.getAttribute('data-gvc-grant') : null;
      if (!d || typeof d.attachChoiceGrant !== 'function' || !id) return;
      d.attachChoiceGrant(id);
    }

    if (row) {
      row.addEventListener('xf:toggle', onRowToggle);
      row.addEventListener('xf:go', onRowGo);
      row.addEventListener('xf:open', onRowCommit);
      row.addEventListener('xf:close', onRowCommit);
    }
    if (attach) attach.addEventListener('click', onAttach);

    /* ---- the row's copy, all of it from the shell ---- */
    function wordFor(state) {
      var n = words ? words.querySelector('.gvc-s-' + state) : null;
      return n ? String(n.textContent || '').trim() : '';
    }
    /* THE CURRENT STATE'S TITLE SPAN, chosen the way drops.css chooses it:
       the count promise while the case is choosable (open, locked), the
       state's own line otherwise. One span, never the concatenated text of
       spans that are mutually exclusive on screen. */
    function titleFor(view) {
      var sel = (view.state === 'open' || view.state === 'locked') ?
        '.gvc-c-' + Math.max(0, Math.min(3, view.count)) : '.gvc-s-' + view.state;
      var n = titles ? titles.querySelector(sel) : null;
      return n ? String(n.textContent || '').trim() : '';
    }

    /* THE FOLDED LINE (BUILD-PLAN.md R8, R9), from the view this paint drew:
       - title: the state's line; LOCKED with money missing names that sum,
         in the owner's sentence for the gift count; a recorded gift names
         itself (its own <bdi>, the one part of the line that shrinks);
       - state: the CLOCK while the case is open and a window runs, else the
         state's word chip;
       - icon: the lock while locked, the gift's own cover once chosen.
       No `auto`, ever: making a choice is not news, and a successful pick
       neither folds nor opens the row. */
    function rowModel(view) {
      var m = {
        hidden: false, tone: 'amber', bar: null, icon: 'gift',
        status: ROW_STATUS[view.state] || 'idle'
      };
      var c = view.condition;
      if (view.state === 'locked' && c && c.remaining_agorot !== null && c.remaining_agorot > 0) {
        var tpl = panel.getAttribute('data-gvc-copy-row-amount-' +
          (view.count >= 3 ? 3 : (view.count === 2 ? 2 : 1)));
        if (tpl) m.titleHtml = fillCopy(tpl, '{amount}', '<span class="ltr">' + esc(money(c.remaining_agorot)) + '</span>');
      }
      if (view.state === 'chosen' && view.receipt && view.receipt.name) {
        m.titleHtml = '<bdi class="xf-rw">' + esc(view.receipt.name) + '</bdi>';
      }
      if (m.titleHtml == null) m.title = titleFor(view);

      if (view.state === 'open' && view.left) {
        m.state = { kind: 'num', html: '<span class="ltr">' + esc(view.left) + '</span>' };
      } else {
        var w = wordFor(view.state);
        m.state = w ? { kind: 'word', text: w } : null;
      }

      if (view.state === 'locked') m.icon = LOCK_ICON;
      if (view.state === 'chosen' && view.receipt) {
        /* the receipt's cover is already validated by coverValue() into a
           url("…") value; the row shows the same picture as an <img> */
        var u = /^url\("(.*)"\)$/.exec(view.receipt.cover || '');
        m.icon = u ? '<img src="' + esc(u[1]) + '" alt="">' : 'check';
      }
      return m;
    }

    function syncRow(view, tease) {
      var f = fold();
      if (!row || !f) return;
      if (!view) {
        /* copied in drops-mystery.js and drops-ticket.js: keep in step (EXO-4824 D-1) */
        /* GONE WHILE OPEN: fold first, which hands any focus inside the case
           back to the row, then hide. Folding is never how availability is
           written, and availability is never written by folding. */
        if (rowOpen()) f.close(row);
        f.set(row, { hidden: true });
        return;
      }
      var m = rowModel(view);
      /* THE POPUP TEASE IS "LEADS ELSEWHERE": the row's own go mark, written
         here because ExonFold.set has no key for it (BUILD-PLAN.md R5, D4).
         A row that starts teasing while open (the popup's offers arrived)
         folds: a lead-elsewhere row is never shown open. */
      if (rowBtn) {
        if (tease && !rowBtn.hasAttribute('data-xf-go')) rowBtn.setAttribute('data-xf-go', '');
        if (!tease && rowBtn.hasAttribute('data-xf-go')) rowBtn.removeAttribute('data-xf-go');
      }
      if (tease && rowOpen()) f.close(row);
      f.set(row, m);
      /* copied in drops-mystery.js and drops-ticket.js: keep in step (EXO-4824 D-1) */
      /* A TICKING CLOCK IS NOT THE ROW'S NAME. fold.js leaves the state slot
         in the button's accessible name (BUILD-PLAN.md D3), so while it
         carries the seconds it is hidden from assistive tech here; the case
         announces through its own live region. */
      if (rowState) {
        var ticking = !!(m.state && m.state.kind === 'num');
        if (ticking && rowState.getAttribute('aria-hidden') !== 'true') rowState.setAttribute('aria-hidden', 'true');
        if (!ticking && rowState.hasAttribute('aria-hidden')) rowState.removeAttribute('aria-hidden');
      }
    }

    function paint(view) {
      if (!view) { syncRow(null, false); mount.hidden = true; return; }
      mount.hidden = false;

      var tease = teasing();
      /* the folded line first: its first write is also when the controller
         folds a server-open row (fold.js ready()), so the inner disclosure
         below reads the row as it really is */
      syncRow(view, tease);
      panel.setAttribute('data-gvc-state', view.state);
      panel.setAttribute('data-gvc-count', String(view.count));
      var isOpen = view.open && rowOpen() && !tease;
      panel.setAttribute('data-gvc-open', isOpen ? 'true' : 'false');
      panel.setAttribute('data-gvc-tease', tease ? 'true' : 'false');
      show(teaseNode, tease && !view.receipt);

      var when = mount.querySelector('[data-gvc-when]');
      show(when, !!view.left);
      setText(mount.querySelector('[data-gvc-cd]'), view.left || '');

      var cond = mount.querySelector('[data-gvc-cond]');
      show(cond, !!view.condition);
      if (view.condition) {
        cond.setAttribute('data-gvc-cond-wording', view.condition.wording);
        setText(mount.querySelector('[data-gvc-cond-amount]'),
          money(view.condition.minimum_subtotal_agorot));
        setText(mount.querySelector('[data-gvc-cond-left]'),
          view.condition.wording === 'left' ? money(view.condition.remaining_agorot) : '');
      }
      /* THE VEIL NAMES THE REAL SHORTFALL: the amount while one is missing,
         in the whole r4 sentence ("עוד ₪60 והמתנה נפתחת", the amount in its
         own mono run, so that one is markup), otherwise the missing items as
         plain text, as before — never "₪0". Written only when it changed, so
         the seconds tick does not rewrite it. */
      var veil = mount.querySelector('[data-gvc-veil]');
      var veilHtml = null, veilPlain = '';
      if (view.condition && view.condition.remaining_agorot !== null && view.condition.remaining_agorot > 0) {
        var amt = '<b class="ltr">' + esc(money(view.condition.remaining_agorot)) + '</b>';
        var sentence = veil ? veil.getAttribute('data-gvc-copy-amount') : '';
        veilHtml = sentence ? fillCopy(sentence, '{amount}', amt) : amt;
      } else if (view.condition && view.condition.remaining_items !== null &&
          view.condition.remaining_items > 0 && veil) {
        veilPlain = (veil.getAttribute('data-gvc-copy-items') || '')
          .split('{items}').join(String(view.condition.remaining_items));
      }
      var veilText = mount.querySelector('[data-gvc-veil-text]');
      var veilKey = veilHtml !== null ? 'h:' + veilHtml : 't:' + veilPlain;
      if (veilText && veilKey !== veilDrawn) {
        if (veilHtml !== null) veilText.innerHTML = veilHtml;
        else veilText.textContent = veilPlain;
        veilDrawn = veilKey;
      }

      /* THE BAYS. Redrawn only when the offer actually changed, so a repaint
         on a cart event does not restart the case's own transitions under the
         customer's pointer. */
      var ids = [];
      for (var n = 0; n < view.bays.length; n++) ids.push(view.bays[n].option_id);
      var key = ids.join('\x00');
      if (key !== drawn) {
        drawn = key;
        for (var i = 0; i < view.bays.length; i++) {
          var b = view.bays[i];
          var node = bayFor(i);
          if (!node) break;
          node.hidden = false;
          node.setAttribute('data-gvc-option', b.option_id);
          node.setAttribute('aria-pressed', 'false');
          node.style.setProperty('--accent', b.accent || '255,170,90');
          /* r4: each gift in its own colour — the ambient wash behind the
             cover is the cover itself, blurred (drops.css .gvc-amb) */
          node.style.setProperty('--cov', b.cover || 'none');
          var art = node.querySelector('[data-gvc-bay-art]');
          if (art) art.style.backgroundImage = b.cover || '';
          setText(node.querySelector('[data-gvc-bay-name]'), b.name);
          setText(node.querySelector('[data-gvc-bay-sub]'), b.sub);
          node.setAttribute('data-gvc-announce', b.name);
        }
        for (var j = view.bays.length; j < bays.length; j++) bays[j].hidden = true;
      }
      /* a case whose write is in flight, or whose condition is provably unmet,
         takes no further taps — but it is still READ, so it is disabled rather
         than removed. */
      var disabled = view.state === 'committing' || view.state === 'locked';
      for (var k = 0; k < bays.length; k++) {
        bays[k].setAttribute('aria-disabled', disabled ? 'true' : 'false');
        bays[k].disabled = disabled;
      }

      /* THE RECEIPT */
      show(mini, !!view.receipt);
      if (view.receipt) {
        mini.setAttribute('data-gvc-grant', view.receipt.choice_grant_id);
        setText(mount.querySelector('[data-gvc-prize]'), view.receipt.name);
        var mart = mount.querySelector('[data-gvc-art]');
        if (mart) mart.style.backgroundImage = view.receipt.cover || '';
        show(attach, view.receipt.attachable);
        show(kept, !view.receipt.attachable);
      }
    }

    function repaintOne(nowMs) {
      var r = current(nowMs);
      report(r.reason);
      paint(r.view);
    }

    return { repaint: repaintOne, mount: mount };
  }

  /* ============================================================
     THE PAGE
     ============================================================ */
  var panels = [];
  var ticker = null;

  function repaint() {
    var now = Date.now();
    var running = false;
    for (var i = 0; i < panels.length; i++) {
      panels[i].repaint(now);
      if (!panels[i].mount.hidden) running = true;
    }
    /* THE CLOCK TICKS WHILE A MOUNT IS AVAILABLE, folded or not: each tick's
       paint also writes the folded row's clock (BUILD-PLAN.md R11), so the
       countdown keeps running in the 48px line with the case closed. */
    if (running && !ticker) ticker = setInterval(repaint, 1000);
    if (!running && ticker) { clearInterval(ticker); ticker = null; }
    if (running !== drew) { drew = running; handOverToTicket(); }
  }
  var drew = false;

  function boot() {
    var mounts = all('[data-drop-anchor]');
    if (!mounts.length) return;
    for (var i = 0; i < mounts.length; i++) {
      var p = Panel(mounts[i]);
      if (p) panels.push(p);
    }
    if (!panels.length) return;

    /* the overlay shells the mounts live inside: their [hidden] flag is the
       only "am I on screen" signal upsell.js publishes, and watching the
       attribute keeps this island uncoupled from that one. */
    var shells = all('[data-exon-popup], [data-exon-drawer]');
    /* copied in drops-mystery.js boot(): keep in step (EXO-4824 D-1) */
    /* [data-no-offers] is written on the popup's MODAL, not on its shell
       (upsell.js), and it decides at runtime whether the popup row teases
       (leads to the cart) or opens: the modal is watched for it, so the
       row's go mark follows the mode rather than the next cart event. */
    var modals = all('[data-up-modal]');
    if (window.MutationObserver && (shells.length || modals.length)) {
      var mo = new MutationObserver(function () { repaint(); });
      for (var j = 0; j < shells.length; j++) {
        mo.observe(shells[j], { attributes: true, attributeFilter: ['hidden', 'data-no-offers'] });
      }
      for (var k = 0; k < modals.length; k++) {
        mo.observe(modals[k], { attributes: true, attributeFilter: ['data-no-offers'] });
      }
    }

    document.addEventListener('exon:drops:state', repaint);
    document.addEventListener('exon:drops:choice-action', repaint);
    document.addEventListener('exon:cart', repaint);
    repaint();
    /* THE TICKET ASKS US, SO IT MUST BE ASKED AGAIN AFTER WE ANSWER. Both
       islands listen to the same events, and document listeners fire in
       registration order — drops-ticket.js registered first, so it decided
       before this island had drawn anything. One follow-up repaint of the
       ticket, after ours, is what keeps "the case replaces the ticket" true on
       the first paint rather than on the second event. */
    handOverToTicket();
  }

  function handOverToTicket() {
    var t = window.ExonDropTicket;
    if (t && typeof t.repaint === 'function') {
      try { t.repaint(); } catch (e) { /* the ticket's own fault channel has it */ }
    }
  }

  try {
    boot();
  } catch (e) {
    fault('anchor_boot_threw', e);
  }

  /* IS THIS SURFACE ACTUALLY DRAWING? drops-ticket.js asks before it stands
     down, and the honest answer is about MOUNTED, VISIBLE PANELS rather than
     about the mechanic string: an Anchor campaign whose island never mounted,
     whose writer is missing or whose state is not renderable must leave the
     ticket in place, or an entitled member sees nothing at all. */
  function drawing() {
    for (var i = 0; i < panels.length; i++) {
      if (!panels[i].mount.hidden) return true;
    }
    return false;
  }

  /* exported for direct testing, the same way the bar and the ticket export
     their stages. `derive` is the pure one. */
  window.ExonDropAnchor = { derive: derive, repaint: repaint, drawing: drawing };
})();
